/**
 * Widget Data Formatting Utilities
 *
 * Shared utilities for formatting widget data across Dashboard and
 * ExportTable components.
 * These functions handle:
 * - Number formatting with decimals and locale
 * - Unit prefix handling (thousand, million, billion)
 * - Equivalent unit conversion
 * - KPI option and widget tag formatting
 * - Top-N filtering
 * - Label parsing and date formatting
 */

import {
  Dataset,
  DatasetType,
  KpiFormat,
  DefaultDatasetData,
  FormattedWidgetData,
  FormattedWidgetDataDataset,
  ListDatasetData,
  DatasetDataValue,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  TWidgetSettings,
  WidgetData,
  WidgetDataLabel,
  WidgetType,
  StatusData
} from 'types/GlobalWidget'
import {
  ComparativeDisplayType,
  KpiOptionObject,
  UnitPrefix
} from 'types/GlobalKpiOption'
import { VariantOption } from 'types/GlobalVariantOption'
import type { TAttributeDataType } from '@homepal/api-contract'
import { getParsedDate, getParsedPartOfDate } from 'utils/dateParser'
import { AUTO_SIGNIFICANT_FIGURES, resolveAutoPrefix } from 'utils/autoFormat'
import { getEffectiveDecimals } from 'utils/numbers'
import { Lang } from 'language/translator'
import { getUnitLabel } from 'utils/unitLabel'

// identifies the column holding every row's total, which a
// horizontal table shows instead of a total row
export const TOTAL_COLUMN_ID = 'TOTAL_COLUMN'

/**
 * Helper to check if value is empty/null
 */
const emptyCheck = (value: string | number | null | undefined): boolean => {
  return value == null || value === 'null' || value === ''
}

/**
 * Parse a single label with proper formatting
 * Handles date parsing and null values
 */
export const parseLabel = (
  dataLabel: {
    label: string | number | null
    technical_name: string
    type: TAttributeDataType
  },
  lang: Lang = 'sv',
  dateShortFormat = false
): string => {
  if (emptyCheck(dataLabel.label)) {
    return 'Saknar värde'
  }

  if (
    dataLabel.technical_name &&
    ['month', 'weekday', 'year', 'year_quarter', 'year_week'].includes(
      dataLabel.technical_name
    )
  ) {
    return getParsedPartOfDate(
      dataLabel.label as number,
      dataLabel.technical_name,
      lang
    )
  }

  if (dataLabel.type === 'DATE') {
    return getParsedDate(dataLabel.label as string | null, lang, {
      year: dateShortFormat ? 'short' : 'full'
    })
  }

  return dataLabel.label ? dataLabel.label.toString() : ''
}

/**
 * Parse widget data label with path segments
 */
export const parseWidgetDataLabel = (
  dataLabel: WidgetDataLabel,
  lang: Lang,
  index: number
): ParsedWidgetDataLabel => {
  const parsedPath: ParsedSegmentPath[] = dataLabel.path.map((path) => ({
    ...path,
    display_label: parseLabel(path, lang)
  }))

  return {
    ...dataLabel,
    index: index,
    display_label: parseLabel(dataLabel, lang),
    path: parsedPath
  }
}

/**
 * Calculate value with unit prefix (thousand, million, billion)
 * and optional equivalent factor
 */
const getPrefixValue = (
  value: number | null,
  unitPrefix: UnitPrefix | null,
  equivalentFactor?: number | null
): number | null => {
  if (value === null) {
    return null
  }

  let divideBy = 1

  switch (unitPrefix) {
  case UnitPrefix.THOUSAND:
    divideBy = 1000
    break
  case UnitPrefix.MILLION:
    divideBy = 1000000
    break
  case UnitPrefix.BILLION:
    divideBy = 1000000000
    break
  default:
    break
  }

  const newValue = value / divideBy

  if (equivalentFactor) {
    return newValue * equivalentFactor
  } else {
    return newValue
  }
}

/**
 * Format a single value based on dataset type, prefix, and decimals
 */
const formatValue = (
  value: number | string[] | number[] | null,
  datasetType: DatasetType,
  unitPrefix: UnitPrefix | null,
  numberOfDecimals: number,
  toLocalString = true,
  equivalentFactor?: number | null,
  significantFigures?: number | null
): string | number | string[] | number[] | null => {
  if (datasetType === DatasetType.NUMBER_TAG) {
    return (value as number[])
      .map((v) =>
        formatValue(
          v,
          DatasetType.NUMBER,
          unitPrefix,
          numberOfDecimals,
          toLocalString,
          equivalentFactor,
          significantFigures
        )
      )
      .filter(Boolean) as string[]
  }

  if (datasetType !== DatasetType.NUMBER) {
    // no parsing for non number dataset types.
    return value
  }

  const prefixValue: number | null = getPrefixValue(
    value as number | null,
    unitPrefix,
    equivalentFactor
  )

  if (prefixValue !== null) {
    const decimals = significantFigures
      ? getEffectiveDecimals(prefixValue, significantFigures)
      : numberOfDecimals

    if (toLocalString) {
      let parsedString = prefixValue.toLocaleString('sv-SE', {
        maximumFractionDigits: decimals
      })

      if (parsedString === '−0' || parsedString === '-0') {
        parsedString = '0'
      }

      return parsedString
    }

    return parseFloat(prefixValue.toFixed(decimals))
  }

  return null
}

/**
 * Filter data array to top N items
 */
const filterTopN = <T>(topNLimit: number | null, data: T[]): T[] => {
  if (topNLimit) {
    return data.slice(0, topNLimit)
  }

  return data
}

/**
 * How a metric's values are presented, gathered in one place so a
 * dataset and a single cell resolve it the same way. With automatic
 * formatting the prefix is picked from autoValues, the values shown
 * together with this one.
 */
export function getKpiFormat(
  kpi: KpiOptionObject | undefined,
  type: DatasetType,
  unit: string | null,
  autoValues: DatasetDataValue[] = []
): KpiFormat {
  const autoFormat = !!kpi?.auto_format
  const prefixOptions = kpi?.unit_prefix.options ?? []
  const unitPrefix = autoFormat
    ? {
        selected: resolveAutoPrefix(
          autoValues.filter((v): v is number => typeof v === 'number'),
          unit === '%' ? [] : prefixOptions
        ),
        options: prefixOptions
      }
    : kpi?.unit_prefix || { selected: null, options: [] }
  const equivalent = autoFormat
    ? { factor: null, unit: null }
    : kpi?.equivalent ?? { factor: null, unit: null }

  return {
    comparativeDisplayTypeSelected:
      kpi?.comparative_display_type.selected || null,
    equivalentFactor: equivalent.factor || null,
    increaseIsPositive: kpi?.increase_is_positive ?? true,
    lowerBoundThreshold: kpi?.lower_bound_threshold ?? null,
    numberOfDecimals: kpi?.number_of_decimals || 0,
    significantFigures: autoFormat ? AUTO_SIGNIFICANT_FIGURES : null,
    type,
    unit:
      type === DatasetType.NUMBER
        ? getUnitLabel(unit, unitPrefix, equivalent.unit || null)
        : unit,
    unitPrefix: unitPrefix.selected,
    upperBoundThreshold: kpi?.upper_bound_threshold ?? null
  }
}

/**
 * Format a single value as a metric writes its numbers. Pass
 * toLocalString false for a number that is compared, not shown.
 */
export function formatKpiValue(
  value: DatasetDataValue,
  format: KpiFormat,
  toLocalString = true
): string | number | string[] | number[] | null {
  return formatValue(
    value,
    format.type,
    format.unitPrefix,
    format.numberOfDecimals,
    toLocalString,
    format.equivalentFactor,
    format.significantFigures
  )
}

/** A display type that shows a comparison, rather than hiding it. */
export function displaysComparison(
  displayType: ComparativeDisplayType | null | undefined
): boolean {
  return !!displayType && displayType !== ComparativeDisplayType.HIDDEN
}

/**
 * A percentage variant option reads as a plain percentage: no unit
 * prefix and no thresholds, whichever metric the row belongs to.
 */
function asPercentageVariant(
  format: KpiFormat,
  option: VariantOption
): KpiFormat {
  return {
    ...format,
    lowerBoundThreshold: null,
    numberOfDecimals: option.number_of_decimals,
    unit: '%',
    unitPrefix: null,
    upperBoundThreshold: null
  }
}

/**
 * A value in a variant column is labelled by the row's own metric:
 * its unit, prefix and equivalent, divided by the column's
 * distribution. A distributed value is small, so it carries no
 * prefix.
 */
function withVariantUnit(
  format: KpiFormat,
  kpi: KpiOptionObject,
  option: VariantOption
): KpiFormat {
  if (format.type !== DatasetType.NUMBER) {
    return format
  }

  const unitPrefix = option.active_show_per ? null : format.unitPrefix

  const unit =
    kpi.unit && option.show_per_unit
      ? `${kpi.unit}/${option.show_per_unit}`
      : kpi.unit

  return {
    ...format,
    numberOfDecimals: option.number_of_decimals,
    unit: getUnitLabel(
      unit,
      { selected: unitPrefix, options: kpi.unit_prefix.options },
      kpi.auto_format ? null : kpi.equivalent.unit || null
    ),
    unitPrefix
  }
}

/**
 * A cell in a variant table is written as its metric, overridden by
 * its comparison figure: the option's decimals, distribution and
 * comparison on top of the metric's format. A percentage column reads
 * as a percentage whichever metric the row belongs to.
 */
export function applyVariantOptionFormat(
  format: KpiFormat,
  kpi: KpiOptionObject,
  option: VariantOption
): KpiFormat {
  const withUnit = option.is_percentage
    ? asPercentageVariant(format, option)
    : withVariantUnit(format, kpi, option)

  return {
    ...withUnit,
    comparativeDisplayTypeSelected: option.comparative_display_type.selected
  }
}

/**
 * Format a single dataset with KPI/tag settings
 */
export function formatDataset(
  dataset: Dataset,
  settings: TWidgetSettings,
  data: WidgetData | null | undefined,
  topNLimit: number | null,
  autoValuesByUnit: Record<string, DatasetDataValue[]> = {}
): FormattedWidgetDataDataset {
  const kpi = settings.kpi_options.find(
    (kpi) => kpi.id === dataset.kpi_option_id
  )
  const tag = settings.tags.selected.find(
    (t) => t.tag_option_id === dataset.tag_option_id
  )

  const variantOption = dataset.variant_option_id
    ? settings.variant_options.find(
      (option) => option.id === dataset.variant_option_id
    )
    : undefined

  // the total column spans every metric, so it is identified by what
  // it holds rather than by the metric it was measured through
  const id = dataset.is_total
    ? TOTAL_COLUMN_ID
    : kpi
      ? kpi.id
      : (tag?.id as string)
  const settingIndex = kpi ? kpi.index : (tag?.index as number)

  const values = filterTopN(topNLimit, dataset.data)
  const autoValuesOf = (unit: string | null) =>
    autoValuesByUnit[unit ?? ''] ?? values

  const baseFormat = getKpiFormat(
    kpi,
    dataset.type,
    dataset.unit,
    autoValuesOf(dataset.unit)
  )
  const format =
    variantOption && kpi
      ? applyVariantOptionFormat(baseFormat, kpi, variantOption)
      : baseFormat
  const { numberOfDecimals } = format
  const hidden = kpi?.hidden || false

  // A pivoted column spans every metric, so each value is written as
  // the metric of the row it sits on rather than as the column's.
  const rowFormats = data?.expandable_metric_rows
    ? filterTopN(topNLimit, data.labels.data).map((label) => {
      // a horizontal variant table has the metric on the row and the
      // comparison figure on the column, a vertical one the reverse
      const rowKpi = settings.kpi_options.find(
        (option) => option.id === label.kpi_option_id
      )
      const rowVariant = label.variant_option_id
        ? settings.variant_options.find(
          (option) => option.id === label.variant_option_id
        )
        : undefined

      const cellKpi = rowKpi ?? kpi
      const cellVariant = variantOption ?? rowVariant

      const rowFormat = getKpiFormat(
        cellKpi,
        dataset.type,
        cellKpi?.unit ?? null,
        autoValuesOf(cellKpi?.unit ?? null)
      )

      return cellVariant && cellKpi
        ? applyVariantOptionFormat(rowFormat, cellKpi, cellVariant)
        : rowFormat
    })
    : null

  const formatOf = (index: number) => rowFormats?.[index] ?? format

  let totalValue = null
  let totalRaw: number | null = null
  let totalPercentageDiff: number | null = null

  if (data?.totals && dataset.kpi_option_id) {
    const total =
      data.totals.find(
        (t) =>
          t.kpi_option_id === dataset.kpi_option_id && t.label === dataset.label
      ) || null

    totalValue = total
      ? (formatKpiValue(total.data.value, format) as string | number | null)
      : 0

    totalRaw = total?.data.value ?? 0

    totalPercentageDiff =
      total?.data.percentage != null
        ? (formatValue(
            total.data.percentage,
            dataset.type,
            null,
            numberOfDecimals,
            false,
            null
          ) as number | null)
        : null
  }

  return {
    id,
    data: values.map((value, i) =>
      formatKpiValue(value, formatOf(i), false)
    ) as DefaultDatasetData | ListDatasetData,
    rawData: values,
    prettyData: values.map((value, i) => formatKpiValue(value, formatOf(i))),
    percentages: dataset.percentages
      ? filterTopN<number | null>(topNLimit, dataset.percentages).map(
        (value) =>
            formatValue(
              value,
              dataset.type,
              null,
              numberOfDecimals,
              false,
              null
            ) as number | null
      )
      : undefined,
    ...format,
    hidden,
    index: dataset.index,
    settingIndex,
    isComparativeData: dataset.is_comparative_data,
    kpiOptionId: kpi?.id,
    label: dataset.label,
    showComparativeValue:
      !!settings.comparative_period.selected.value &&
      format.comparativeDisplayTypeSelected === ComparativeDisplayType.VALUE,
    statuses: filterTopN<StatusData>(topNLimit, dataset.statuses),
    total: totalValue,
    totalRaw,
    totalPercentageDiff,
    isTotal: dataset.is_total ?? false,
    variantOptionId: dataset.variant_option_id
  }
}

/**
 * Format all datasets for a widget
 */
function formatDatasets(
  datasets: Dataset[],
  settings: TWidgetSettings,
  data: WidgetData | null | undefined,
  topNLimit: number | null
): FormattedWidgetDataDataset[] {
  const shown = datasets.filter(
    (dataset) =>
      settings.kpi_options.find((kpi) => kpi.id === dataset.kpi_option_id) ||
      settings.tags.selected.find(
        (t) => t.tag_option_id === dataset.tag_option_id
      )
  )

  // datasets in the same unit share an axis, so they share a prefix
  const autoValuesByUnit = shown.reduce<Record<string, DatasetDataValue[]>>(
    (acc, dataset) => {
      const key = dataset.unit ?? ''

      acc[key] = [...(acc[key] ?? []), ...filterTopN(topNLimit, dataset.data)]

      return acc
    },
    {}
  )

  return shown.map((dataset) =>
    formatDataset(dataset, settings, data, topNLimit, autoValuesByUnit)
  )
}

/**
 * Main function: Format widget data with settings
 *
 * Takes raw widget data and settings, returns formatted data ready for display.
 * Handles:
 * - Top-N filtering
 * - Label parsing
 * - Dataset formatting with decimals, units, prefixes
 * - Link and coordinate data
 *
 * @param data - Raw widget data from API
 * @param settings - Widget settings (KPI options, tags, etc)
 * @returns Formatted data ready for visualization
 */
export function formatWidgetData(
  data: WidgetData | null | undefined,
  settings: TWidgetSettings,
  lang: Lang = 'sv'
): FormattedWidgetData {
  if (
    data &&
    settings.type.selected !== WidgetType.KEY_FIGURE &&
    settings.type.selected !== WidgetType.FUNNEL
  ) {
    const topNLimit = settings.top_n_limit
    let topLevelCounter = 0

    // find index of toplevel label
    const labelIndex =
      topNLimit !== null
        ? data.labels.data.findIndex((item) => {
          if (item.path.length === 0) {
            topLevelCounter += 1

            return topLevelCounter === topNLimit + 1
          }

          return false
        })
        : null

    const limit = labelIndex === -1 ? null : labelIndex

    const labels: WidgetDataLabel[] = filterTopN(limit, data.labels.data)
    const links: (string | null)[] = filterTopN(limit, data.external_links)
    const coordinates = filterTopN(limit, data.coordinates)

    return {
      coordinates,
      labels: labels.map((label, index) =>
        parseWidgetDataLabel(label, lang, index)
      ),
      links: links,
      datasets: formatDatasets(data.data, settings, data, limit),
      expandableMetricRows: data.expandable_metric_rows === true
    }
  }

  return {
    coordinates: [],
    labels: [],
    links: [],
    datasets: [],
    expandableMetricRows: false
  }
}

// Type guard to check if is formatted data
export const isFormattedData = (
  data: FormattedWidgetData | null
): data is FormattedWidgetData => {
  return !!data
}
